import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ContractTemplateDto,
  ViewDocumentAsOwnerDto,
  useCreateContractFromPublicBidMutation,
  useListContractTemplatesQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { SelectorValue } from "../../../../common/input/Selector/SelectorValue";
import { SingleFormSelector } from "../../../../common/input/Selector/SingleFormSelector";
import { ModalSkeleton } from "../../../../common/ModalSkeleton";
import { urls } from "../../../../urls";

interface Props {
  eventId: string;
  bidId: string;
  supplierName: string;
  defaultTitle: string;
  documents: ViewDocumentAsOwnerDto[];
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  title: string;
  template: SelectorValue | null;
}

export const CreateContractFromBidModal = ({
  eventId,
  bidId,
  supplierName,
  defaultTitle,
  documents,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const navigate = useNavigate();
  const toast = useToast();
  const apiError = useApiError();

  // Default to copying every procurement document; reset to "all" whenever the modal is (re)opened.
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>(() => documents.map((d) => d.id));
  useEffect(() => {
    if (isOpen) setSelectedDocumentIds(documents.map((d) => d.id));
  }, [isOpen, documents]);

  const toggleDocument = (id: string, checked: boolean) =>
    setSelectedDocumentIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((d) => d !== id)));

  const [createContract, { isLoading }] = useCreateContractFromPublicBidMutation();
  const { data: templatesRes, isLoading: isLoadingTemplates } = useListContractTemplatesQuery({
    organizationId: authState.selectedOrg.id,
    orgId: authState.selectedOrg.id,
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { title: defaultTitle, template: null } });

  const getTemplateName = (template: ContractTemplateDto): string => template.name ?? t("Contract");

  const submit = async (values: FormValues) => {
    try {
      const result = await createContract({
        eventId,
        bidId,
        createContractFromPublicBidRequest: {
          title: values.title,
          templateId: values.template?.value ?? undefined,
          documentIds: selectedDocumentIds,
        },
      }).unwrap();
      toast({ title: t("Contract created successfully"), status: "success", position: "top-right" });
      onClose();
      navigate(urls.contracts.edit.go(result.id, eventId).details);
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("New contract with")} {supplierName}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoadingTemplates ? (
            <ModalSkeleton />
          ) : (
            <form id="create-contract-from-bid-form" onSubmit={handleSubmit(submit)}>
              <FormControl isInvalid={errors.title !== undefined} pt="2.5">
                <FormLabel htmlFor="title">{t("Contract title")}</FormLabel>
                <Input
                  id="title"
                  backgroundColor="smBackground"
                  {...register("title", {
                    required: t("Please provide a title for your contract") ?? "",
                    minLength: { value: 2, message: t("Must be at least 2 characters long") },
                  })}
                />
                <FormErrorMessage>{errors.title && errors.title.message}</FormErrorMessage>
                <FormHelperText>{t("Please provide a title for your contract")}</FormHelperText>
              </FormControl>
              <Flex pt="4">
                {templatesRes && (
                  <SingleFormSelector
                    options={templatesRes.templates.map((tpl) => ({ label: getTemplateName(tpl), value: tpl.id }))}
                    name="template"
                    label={t("Select type")}
                    helperText={t("Select a type") ?? ""}
                    control={control}
                    placeholderText=""
                    noOptionsProvidedMessage={t("No types provided")}
                    noMatchingOptionsMessage={t("No matching types")}
                    errorMessage={undefined}
                  />
                )}
              </Flex>
              {documents.length > 0 && (
                <FormControl pt="4">
                  <FormLabel>{t("Documents to copy to the contract")}</FormLabel>
                  <Stack spacing="2" maxH="64" overflowY="auto" pr="2">
                    {documents.map((doc) => (
                      <Checkbox
                        key={doc.id}
                        isChecked={selectedDocumentIds.includes(doc.id)}
                        onChange={(e) => toggleDocument(doc.id, e.target.checked)}
                      >
                        <Text as="span" color="smText" fontSize="sm">
                          {doc.name}
                        </Text>
                      </Checkbox>
                    ))}
                  </Stack>
                  <FormHelperText>{t("Selected documents are copied onto the new contract.")}</FormHelperText>
                </FormControl>
              )}
              <Flex width="full" justify="end">
                <Button type="submit" form="create-contract-from-bid-form" colorScheme="teal" isLoading={isLoading} mt="8">
                  {t("Create contract")}
                </Button>
              </Flex>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
